import React, { Component, ErrorInfo, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';

import { AuthProvider } from 'contexts/auth-context';
import { ThemeContextProvider } from 'contexts/theme-context';
import { SaveToastProvider } from 'contexts/save-toast-context';
import { useNProgress } from 'hooks/use-nprogress';

import 'simplebar-react/dist/simplebar.min.css';
import 'leaflet/dist/leaflet.css';
import 'components/collab-rich-text/quill-theme.scss';
import 'pages/print.scss';

import { handleImpersonationEscapeHatch } from 'utils/impersonation';
import { AppRoutes } from './router';

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Root error boundary caught:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace', color: 'red' }}>
          <h2>Render error — record this, then try a force reload (Ctrl+R / Cmd+R), then tell Phil if it persists</h2>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(error)}</pre>
        </div>
      );
    }
    return children;
  }
}

handleImpersonationEscapeHatch();

const NProgressGate = () => {
  useNProgress();
  return null;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <HelmetProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <ThemeContextProvider>
              <SaveToastProvider>
                <StyledEngineProvider injectFirst>
                  <CssBaseline />
                  <BrowserRouter>
                    <QueryParamProvider adapter={ReactRouter6Adapter}>
                      <NProgressGate />
                      <AppRoutes />
                    </QueryParamProvider>
                  </BrowserRouter>
                </StyledEngineProvider>
              </SaveToastProvider>
            </ThemeContextProvider>
          </AuthProvider>
        </LocalizationProvider>
      </HelmetProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
);
